import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Clock } from 'lucide-react';
import { 
  radiologyTestService, 
  radiologyOrderService, 
  radiologyOrderItemService,
  RadiologyTest
} from '../../lib/services.radiology';
import { patientService } from '../../lib/services';
import { admissionService, Admission } from '../../lib/services.ipd';
import { ipdService } from '../../lib/pharmacyServices';
import toast from 'react-hot-toast';

interface Patient {
  id: string;
  name: string;
  phone?: string;
}

export default function NewRadiologyOrder() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [tests, setTests] = useState<RadiologyTest[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [selectedTests, setSelectedTests] = useState<RadiologyTest[]>([]);
  const [source, setSource] = useState<'internal' | 'external' | 'self'>('internal');
  const [externalDoctor, setExternalDoctor] = useState('');
  const [priority, setPriority] = useState<'routine' | 'urgent' | 'stat'>('routine');
  const [scheduledAt, setScheduledAt] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeAdmissions, setActiveAdmissions] = useState<Admission[]>([]);
  const [selectedAdmissionId, setSelectedAdmissionId] = useState<string>('');
  const [filterModality, setFilterModality] = useState<string>('all');

  useEffect(() => {
    loadPatients();
    loadTests();
  }, []);

  const loadPatients = async () => {
    try {
      const data = await patientService.getAll();
      setPatients(data);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    }
  };

  const loadTests = async () => {
    try {
      const data = await radiologyTestService.getAll();
      setTests(data);
    } catch (error) {
      console.error('Failed to load tests:', error);
      toast.error('Failed to load radiology tests');
    }
  };

  const loadActiveAdmissionsForPatient = async (patientId: string) => {
    if (!patientId) {
      setActiveAdmissions([]);
      setSelectedAdmissionId('');
      return;
    }
    try {
      const allActive = await admissionService.getActive();
      const patientAdmissions = allActive.filter(a => a.patient_id === patientId);
      setActiveAdmissions(patientAdmissions);
      if (patientAdmissions.length === 1) {
        setSelectedAdmissionId(patientAdmissions[0].id);
      } else {
        setSelectedAdmissionId('');
      }
    } catch (error) {
      console.error('Failed to load admissions:', error);
    }
  };

  const filteredPatients = patients.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTests = filterModality === 'all' 
    ? tests 
    : tests.filter(t => t.modality === filterModality);

  const handleTestToggle = (test: RadiologyTest) => {
    if (selectedTests.find(t => t.id === test.id)) {
      setSelectedTests(selectedTests.filter(t => t.id !== test.id));
    } else {
      setSelectedTests([...selectedTests, test]);
    }
  };

  const calculateTotal = () => {
    return selectedTests.reduce((sum, test) => sum + test.price, 0);
  };

  const calculateDueAt = (priorityLevel: string, tatHours: number) => {
    const now = new Date();
    let hours = tatHours;
    if (priorityLevel === 'stat') hours = Math.min(2, tatHours);
    if (priorityLevel === 'urgent') hours = Math.min(4, tatHours);
    now.setHours(now.getHours() + hours);
    return now.toISOString();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    if (selectedTests.length === 0) {
      toast.error('Please select at least one test');
      return;
    }

    if (source === 'external' && !externalDoctor.trim()) {
      toast.error('Please enter external doctor name');
      return;
    }

    setLoading(true);
    try {
      const order = await radiologyOrderService.create({
        patient_id: selectedPatient.id,
        source,
        external_doctor: source === 'external' ? externalDoctor : undefined,
        priority,
        scheduled_at: scheduledAt || undefined,
        notes,
        status: scheduledAt ? 'scheduled' : 'ordered',
        due_at: calculateDueAt(priority, selectedTests[0]?.tat_hours || 24),
      });

      for (const test of selectedTests) {
        await radiologyOrderItemService.create({
          order_id: order.id,
          test_id: test.id,
          status: 'ordered',
          price: test.price,
          discount: 0,
        });
      }

      if (selectedAdmissionId) {
        try {
          await ipdService.postChargeToBilling(selectedAdmissionId, {
            productId: order.id,
            quantity: selectedTests.length,
            price: calculateTotal(),
            source: 'radiology',
            description: `Radiology order - ${selectedTests.length} study(ies)`
          });
          toast.success('Radiology order created and charges posted to admission');
        } catch (billingError) {
          console.error('Billing integration failed:', billingError);
          toast.error('Radiology order created but billing post failed. Please add charges manually.');
        }
      } else {
        toast.success('Radiology order created successfully');
      }

      navigate('/radiology');
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error('Failed to create radiology order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">New Radiology Order</h1>
        <p className="text-gray-600">Create a new imaging order for a patient</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search patient by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {searchTerm && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                {filteredPatients.map(patient => (
                  <button
                    key={patient.id}
                    type="button"
                    onClick={() => {
                      setSelectedPatient(patient);
                      setSearchTerm('');
                      loadActiveAdmissionsForPatient(patient.id);
                    }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                  >
                    <div className="font-medium">{patient.name}</div>
                    {patient.phone && (
                      <div className="text-sm text-gray-600">{patient.phone}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedPatient && (
              <div className="mt-2 p-3 bg-blue-50 rounded-md flex items-center justify-between">
                <div>
                  <div className="font-medium text-blue-900">{selectedPatient.name}</div>
                  {selectedPatient.phone && (
                    <div className="text-sm text-blue-700">{selectedPatient.phone}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedPatient(null)}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Source <span className="text-red-500">*</span>
              </label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as 'internal' | 'external' | 'self')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="internal">Internal (In-house Doctor)</option>
                <option value="external">External (Referring Doctor)</option>
                <option value="self">Self (Walk-in)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                value={priority}
                onChange={(e) => setPriority(e.target.value as 'routine' | 'urgent' | 'stat')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="stat">STAT</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admission ID (Optional - for IPD billing)
            </label>
            <select
              value={selectedAdmissionId}
              onChange={(e) => setSelectedAdmissionId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">No admission (OPD)</option>
              {activeAdmissions.map((admission) => (
                <option key={admission.id} value={admission.id}>
                  {admission.admission_no} - Ward: {admission.current_bed?.ward?.name || 'N/A'}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Select admission to automatically post charges to IPD billing
            </p>
          </div>

          {source === 'external' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                External Doctor Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={externalDoctor}
                onChange={(e) => setExternalDoctor(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter referring doctor's name"
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule Appointment (Optional)
            </label>
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Imaging Tests <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mb-3 flex-wrap">
              <button
                type="button"
                onClick={() => setFilterModality('all')}
                className={`px-3 py-1 rounded-full text-sm ${
                  filterModality === 'all'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                All
              </button>
              {['XR', 'USG', 'CT', 'MRI', 'MAMMO', 'DXA'].map(mod => (
                <button
                  key={mod}
                  type="button"
                  onClick={() => setFilterModality(mod)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filterModality === mod
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {mod}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
              {filteredTests.map(test => (
                <div
                  key={test.id}
                  onClick={() => handleTestToggle(test)}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedTests.find(t => t.id === test.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{test.name}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        <span className="inline-block px-2 py-0.5 bg-gray-100 rounded text-xs mr-2">
                          {test.modality}
                        </span>
                        {test.body_part && (
                          <span className="text-gray-500">{test.body_part}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
                        <span className="font-semibold text-gray-900">₹{test.price}</span>
                        <span className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {test.tat_hours}h TAT
                        </span>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={!!selectedTests.find(t => t.id === test.id)}
                      onChange={() => {}}
                      className="w-5 h-5 text-blue-600"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedTests.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-2">Selected Tests:</div>
              <div className="space-y-1 mb-3">
                {selectedTests.map(test => (
                  <div key={test.id} className="flex justify-between text-sm">
                    <span>{test.name}</span>
                    <span>₹{test.price}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-300 pt-2 flex justify-between font-semibold">
                <span>Total</span>
                <span>₹{calculateTotal()}</span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter any additional notes or clinical indications..."
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !selectedPatient || selectedTests.length === 0}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Order'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/radiology')}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
